import React from "react";
var __jsx = React.createElement;
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faRectangleList, faSquareCheck } from "@fortawesome/pro-regular-svg-icons";
import { useLetterTemplates } from "@/hooks/letter";
import { useFormsList } from "@/hooks/form";
import { useEmrContext } from "@/hooks/emr-context";
import { useDeleteAction } from "@/hooks/tasks";
import { TagActionsGroup } from "../tag-actions-group";
import { LetterAction } from "../letter-action";
import { FormAction } from "../form-action";
import { TaskAction } from "../task-action";
import { ActionDeleteConfirm } from "../action-delete-confirm";
/** Grouped list of the confirmed decision's actions (tags, letters, forms, tasks) with delete handling. */
export function SuggestedActionsList(_ref) {
  var actions = _ref.actions,
    patientUid = _ref.patientUid,
    reminderId = _ref.reminderId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(new Set()),
    deletedIds = _useState[0],
    setDeletedIds = _useState[1];
  var _useState2 = useState(new Set()),
    doneIds = _useState2[0],
    setDoneIds = _useState2[1];
  var _useState3 = useState(null),
    pendingDeleteId = _useState3[0],
    setPendingDeleteId = _useState3[1];
  var _useDeleteAction = useDeleteAction(patientUid, reminderId),
    deleteAction = _useDeleteAction.mutate,
    isDeleting = _useDeleteAction.isLoading;
  var _useEmrContext = useEmrContext(),
    workspaceId = _useEmrContext.workspaceId;
  var numericUid = patientUid ? Number(patientUid) : null;
  var _useLetterTemplates = useLetterTemplates(workspaceId, true, "letter"),
    letterTemplates = _useLetterTemplates.data;
  var _useFormsList = useFormsList(numericUid !== null && numericUid !== void 0 ? numericUid : 0),
    formsData = _useFormsList.data;
  var visibleActions = actions.filter(function (a) {
    return !deletedIds.has(a.id);
  });
  var tagActions = visibleActions.filter(function (a) {
    return a.type === "tag";
  });
  var letterActions = visibleActions.filter(function (a) {
    return a.type === "letter";
  });
  var formActions = visibleActions.filter(function (a) {
    return a.type === "form";
  });
  var taskActions = visibleActions.filter(function (a) {
    return a.type === "task";
  });
  var requestDelete = function requestDelete(actionId) {
    return setPendingDeleteId(actionId);
  };
  var confirmDelete = function confirmDelete() {
    if (pendingDeleteId === null) return;
    var actionId = pendingDeleteId;
    deleteAction(actionId, {
      onSuccess: function onSuccess() {
        setDeletedIds(function (prev) {
          return new Set(prev).add(actionId);
        });
        setPendingDeleteId(null);
      }
    });
  };
  var handleDone = function handleDone(actionId) {
    return setDoneIds(function (prev) {
      return new Set(prev).add(actionId);
    });
  };
  var handleUndo = function handleUndo(actionId) {
    return setDoneIds(function (prev) {
      var next = new Set(prev);
      next["delete"](actionId);
      return next;
    });
  };
  var isActionDone = function isActionDone(action) {
    return action.state === "done" || doneIds.has(action.id);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-6"
  }, tagActions.length > 0 && /*#__PURE__*/React.createElement(TagActionsGroup, {
    tagActions: tagActions,
    patientUid: patientUid,
    reminderId: reminderId,
    isActionDone: isActionDone,
    onDone: handleDone,
    onUndo: handleUndo,
    onDelete: requestDelete
  }), letterActions.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faEnvelope,
    className: "w-3.5 h-3.5 text-sds-brand-default"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-secondary"
  }, t("label.generate_letters", {
    defaultValue: "Generate letters"
  }))), letterActions.map(function (action) {
    var payload = action.payload;
    var template = letterTemplates === null || letterTemplates === void 0 ? void 0 : letterTemplates.find(function (tpl) {
      return tpl.id === payload.id;
    });
    return /*#__PURE__*/React.createElement(LetterAction, {
      key: action.id,
      action: action,
      template: template,
      patientUid: patientUid,
      reminderId: reminderId,
      isDone: isActionDone(action),
      onDone: function onDone() {
        return handleDone(action.id);
      },
      onUndo: function onUndo() {
        return handleUndo(action.id);
      },
      onDelete: requestDelete
    });
  })), formActions.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faRectangleList,
    className: "w-3.5 h-3.5 text-sds-brand-default"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-secondary"
  }, t("label.complete_form", {
    defaultValue: "Complete form"
  }))), /*#__PURE__*/React.createElement("p", {
    className: "text-style-body-small text-sds-base-secondary"
  }, t("label.complete_form_helper", {
    defaultValue: "Open the form and fill it in for this patient."
  }))), formActions.map(function (action) {
    var payload = action.payload;
    var template = formsData === null || formsData === void 0 ? void 0 : formsData.forms.find(function (f) {
      return f.id === payload.id;
    });
    return /*#__PURE__*/React.createElement(FormAction, {
      key: action.id,
      action: action,
      template: template,
      patientUid: patientUid,
      reminderId: reminderId,
      isDone: isActionDone(action),
      onDone: function onDone() {
        return handleDone(action.id);
      },
      onUndo: function onUndo() {
        return handleUndo(action.id);
      },
      onDelete: requestDelete
    });
  })), taskActions.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faSquareCheck,
    className: "w-3.5 h-3.5 text-sds-brand-default"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-secondary"
  }, t("label.create_tasks", {
    defaultValue: "Create tasks"
  }))), taskActions.map(function (action) {
    return /*#__PURE__*/React.createElement(TaskAction, {
      key: action.id,
      action: action,
      patientUid: patientUid,
      reminderId: reminderId,
      isDone: isActionDone(action),
      onDone: function onDone() {
        return handleDone(action.id);
      },
      onUndo: function onUndo() {
        return handleUndo(action.id);
      },
      onDelete: requestDelete
    });
  })), /*#__PURE__*/React.createElement(ActionDeleteConfirm, {
    opened: pendingDeleteId !== null,
    isDeleting: isDeleting,
    onClose: function onClose() {
      return setPendingDeleteId(null);
    },
    onConfirm: confirmDelete
  }));
}